import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  GamepadIcon, 
  GraduationCap, 
  Users,
  Coins,
  BookOpen,
  Users2,
} from 'lucide-react';
import { useProfile } from '../hooks/useProfile';

const KidsDashboard: FC = () => {
  const navigate = useNavigate();
  const { userProfile, isLoading } = useProfile();

  // Función para calcular el progreso al siguiente nivel
  const calculateNextLevelProgress = () => {
    if (!userProfile?.cerebrix) {
      return { currentLevel: 1, currentPoints: 0, progress: 0, remainingPoints: 100 };
    }
    
    const currentPoints = userProfile.cerebrix;
    const currentLevel = Math.ceil(currentPoints / 100);
    const nextLevelPoints = currentLevel * 100;
    const basePoints = (currentLevel - 1) * 100;
    const progress = ((currentPoints - basePoints) / (nextLevelPoints - basePoints)) * 100;
    const remainingPoints = nextLevelPoints - currentPoints;
    
    console.log('Calculando progreso:', {
      currentPoints,
      currentLevel,
      nextLevelPoints,
      remainingPoints,
      progress,
      userProfile: {
        cerebrix: userProfile.cerebrix,
        level: userProfile.level
      }
    });

    return {
      currentLevel,
      currentPoints,
      remainingPoints,
      progress
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  const { currentLevel, currentPoints, remainingPoints, progress } = calculateNextLevelProgress();

  const menuItems = [
    {
      title: "Lecciones",
      description: "El oro está aquí",
      icon: GraduationCap,
      path: "/lessons",
      color: "from-amber-400 to-yellow-600", // Mantenemos el dorado para Lecciones
      animation: "hover:-rotate-3"
    },
    {
      title: "Juegos",
      description: "Aprende jugando sobre finanzas",
      icon: GamepadIcon,
      path: "/games",
      color: "from-violet-400 to-purple-600", // Nuevo color morado vibrante
      animation: "hover:rotate-3"
    },
    {
      title: "Logros",
      description: "Mira tus medallas y trofeos",
      icon: Trophy,
      path: "/achievements",
      color: "from-rose-400 to-red-600", // Rosa a rojo moderno
      animation: "hover:scale-105"
    },
    {
      title: "Comunidad",
      description: "Conecta con otros estudiantes",
      icon: Users,
      path: "/community",
      color: "from-indigo-400 to-blue-700", // Movido desde Biblioteca
      animation: "hover:scale-105"
    },
    {
      title: "Biblioteca",
      description: "Explora recursos educativos",
      icon: BookOpen,
      path: "/library",
      color: "from-orange-500 to-red-500", // Nuevo naranja vibrante
      animation: "hover:scale-105"
    },
    {
      title: "Socios",
      description: "Únete a nuestro programa",
      icon: Users2,
      path: "/affiliates",
      color: "from-emerald-400 to-teal-600", // Verde esmeralda a verde azulado
      animation: "hover:scale-105"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      {/* Saludo personalizado */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-primary">
          ¡Hola {userProfile?.student_name || 'Estudiante'}! 👋
        </h1>
        <p className="text-gray-600 mt-2">
          Te damos la bienvenida a tu aventura financiera
        </p>
      </div>

      {/* Bienvenida y Nivel */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <p className="text-gray-600 text-lg mb-6">¿Qué quieres aprender hoy?</p>
        
        {/* Sección unificada de Progreso y Puntos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-primary/10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Nivel Actual */}
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-full">
                <Trophy className="h-8 w-8 text-primary animate-pulse" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Nivel {currentLevel}</h3>
                <p className="text-gray-600">¡Sigue así, vas muy bien! 🚀</p>
              </div>
            </div>
            
            {/* Cerebrix Totales */}
            <div className="flex items-center gap-4">
              <div className="bg-secondary/10 p-3 rounded-full">
                <Coins className="h-8 w-8 text-secondary animate-pulse" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-800">{userProfile.cerebrix} Cerebrix</h3>
                  <button
                    onClick={() => navigate('/exchange')}
                    className="bg-secondary hover:bg-secondary/90 text-white px-3 py-1 rounded-full text-sm transition-colors"
                  >
                    Canjear Cerebrix
                  </button>
                </div>
                <p className="text-gray-600">¡Tus logros acumulados! ⭐</p>
              </div>
            </div>
          </div>

          {/* Barra de Progreso */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Progreso al siguiente nivel</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-primary via-secondary to-accent rounded-full"
              />
            </div>
            <div className="flex justify-between items-start mt-4">
              <div className="text-sm text-gray-600">
                <p>Te faltan <span className="font-bold text-secondary">{remainingPoints} Cerebrix</span> para el siguiente nivel</p>
                <ul className="mt-2 space-y-1 text-gray-500 list-disc list-inside">
                  <li>Completa lecciones (+25-100 Cerebrix)</li>
                  <li>Juega minijuegos (+5-50 Cerebrix)</li>
                  <li>Gana logros especiales (+10-100 Cerebrix)</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Menú Principal */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {menuItems.map((item, index) => (
          <motion.div
            key={item.path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`cursor-pointer transform transition-all duration-300 ${item.animation}`}
            onClick={() => navigate(item.path)}
          >
            <div className={`bg-gradient-to-br ${item.color} rounded-xl p-6 text-white shadow-lg h-full`}>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-4 bg-white/20 rounded-full">
                  <item.icon className="h-8 w-8 animate-float" />
                </div>
                <h3 className="text-xl font-bold">{item.title}</h3>
                <p className="text-white/90 text-sm">{item.description}</p>
                {item.subtext && (
                  <p className="text-white/80 text-xs italic mt-2">{item.subtext}</p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default KidsDashboard;
